import { useState, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Save, Trash2 } from 'lucide-react'
import type { MarkPriceAlert } from '@/types'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { TagExpressionInput } from '@/components/common/TagExpressionInput'
import { legacyTagsToExpression } from '@/lib/tagExpression'
import { TelegramDestinationSelect } from '@/components/settings/TelegramDestinationSelect'

interface MarkPriceAlertEditSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  alert?: MarkPriceAlert | null
  onSave: (alert: Partial<MarkPriceAlert>) => Promise<void>
  onDelete?: (id: string) => Promise<void>
}

const PERIOD_OPTIONS = ['1m', '5m', '15m', '30m', '1h', '4h', '1d']

export function MarkPriceAlertEditSheet({
  open,
  onOpenChange,
  alert,
  onSave,
  onDelete,
}: MarkPriceAlertEditSheetProps) {
  const isNew = !alert?.id
  const exchangeOptions = useExchangeOptions('mark_price')

  const [name, setName] = useState('')
  const [exchanges, setExchanges] = useState<string[]>([])
  const [thresholdPct, setThresholdPct] = useState([2])
  const [period, setPeriod] = useState('5m')
  const [enabled, setEnabled] = useState(true)
  const [enableTelegram, setEnableTelegram] = useState(true)
  const [enablePushover, setEnablePushover] = useState(false)
  const [telegramChatId, setTelegramChatId] = useState('')
  const [telegramReplyTo, setTelegramReplyTo] = useState('')
  const [tagExpression, setTagExpression] = useState('')
  const [legacyRequired, setLegacyRequired] = useState<string[]>([])
  const [legacyExcluded, setLegacyExcluded] = useState<string[]>([])

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (alert) {
      setName(alert.name || '')
      setExchanges(alert.exchanges || [])
      setThresholdPct([alert.threshold_pct || 2])
      setPeriod(alert.period || '5m')
      setEnabled(alert.enabled !== false)
      setEnableTelegram(alert.enable_telegram !== false)
      setEnablePushover(alert.enable_pushover === true)
      setTelegramChatId(alert.telegram?.chat_id || '')
      setTelegramReplyTo(String(alert.telegram?.reply_to_message_id ?? ''))
      // Use tag_expression if available, otherwise convert from legacy
      const existingExpression = alert.tag_expression || ''
      if (existingExpression) {
        setTagExpression(existingExpression)
        setLegacyRequired([])
        setLegacyExcluded([])
      } else {
        const required = alert.required_tags || []
        const excluded = alert.exclude_tags || []
        setLegacyRequired(required)
        setLegacyExcluded(excluded)
        if (required.length > 0 || excluded.length > 0) {
          setTagExpression(legacyTagsToExpression(required, [], excluded))
        } else {
          setTagExpression('')
        }
      }
    } else {
      setName('')
      setExchanges([])
      setThresholdPct([2])
      setPeriod('5m')
      setEnabled(true)
      setEnableTelegram(true)
      setEnablePushover(false)
      setTelegramChatId('')
      setTelegramReplyTo('')
      setTagExpression('')
      setLegacyRequired([])
      setLegacyExcluded([])
    }
  }, [alert, open])

  const toggleExchange = (exchange: string) => {
    setExchanges((prev) =>
      prev.includes(exchange)
        ? prev.filter((e) => e !== exchange)
        : [...prev, exchange]
    )
  }

  const handleSave = async () => {
    if (!name.trim() || exchanges.length === 0) return

    setIsSaving(true)
    try {
      const alertData: Partial<MarkPriceAlert> & { tag_expression?: string } = {
        ...(alert?.id && { id: alert.id }),
        name: name.trim(),
        exchanges,
        threshold_pct: thresholdPct[0],
        period,
        enabled,
        enable_telegram: enableTelegram,
        enable_pushover: enablePushover,
        required_tags: [],
        exclude_tags: [],
        tag_expression: tagExpression,
      }

      if (enableTelegram && (telegramChatId || telegramReplyTo)) {
        alertData.telegram = {
          chat_id: telegramChatId,
          reply_to_message_id: telegramReplyTo || undefined,
        }
      }
      await onSave(alertData)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save alert:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!alert?.id || !onDelete) return
    if (!confirm('Are you sure you want to delete this alert?')) return

    setIsDeleting(true)
    try {
      await onDelete(alert.id)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to delete alert:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isNew ? 'Create Mark Price Alert' : 'Edit Mark Price Alert'}</SheetTitle>
          <SheetDescription>
            Configure mark price change monitoring thresholds.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="alert-name">Alert Name</Label>
              <Input
                id="alert-name"
                placeholder="Mark Price Alert"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="alert-enabled">Enabled</Label>
              <Switch
                id="alert-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Exchanges</Label>
            <div className="flex flex-wrap gap-2">
              {exchangeOptions.map((exchange) => (
                <Badge
                  key={exchange}
                  variant={exchanges.includes(exchange) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleExchange(exchange)}
                >
                  {exchange}
                </Badge>
              ))}
            </div>
            {exchanges.length === 0 && (
              <p className="text-xs text-destructive">Select at least one exchange</p>
            )}
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Price Change Threshold</Label>
                <span className="text-sm font-medium">{thresholdPct[0]}%</span>
              </div>
              <Slider
                value={thresholdPct}
                onValueChange={setThresholdPct}
                min={0.5}
                max={20}
                step={0.5}
              />
              <p className="text-xs text-muted-foreground">
                Alert when price changes by this percentage
              </p>
            </div>

            <div className="space-y-2">
              <Label>Monitoring Period</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Notifications */}
          <div className="space-y-4">
            <Label className="text-base">Notifications</Label>

            <div className="flex items-center justify-between">
              <Label htmlFor="enable-telegram">Telegram</Label>
              <Switch
                id="enable-telegram"
                checked={enableTelegram}
                onCheckedChange={setEnableTelegram}
              />
            </div>

            {enableTelegram && (
              <div className="pl-4">
                <div className="space-y-2">
                  <Label htmlFor="telegram-destination">Destination</Label>
                  <TelegramDestinationSelect
                    id="telegram-destination"
                    chatId={telegramChatId}
                    replyToMessageId={telegramReplyTo}
                    onChange={({ chat_id, reply_to_message_id }) => {
                      setTelegramChatId(chat_id)
                      setTelegramReplyTo(reply_to_message_id ? String(reply_to_message_id) : '')
                    }}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label htmlFor="enable-pushover">Pushover</Label>
              <Switch
                id="enable-pushover"
                checked={enablePushover}
                onCheckedChange={setEnablePushover}
              />
            </div>
          </div>

          <Separator />

          {/* Tags */}
          <div className="space-y-4">
            <TagExpressionInput
              value={tagExpression}
              onChange={setTagExpression}
              legacyRequired={legacyRequired}
              legacyExcluded={legacyExcluded}
            />
          </div>
        </div>

        <SheetFooter className="gap-2 sm:gap-0">
          {!isNew && onDelete && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || isSaving}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving || isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isDeleting || !name.trim() || exchanges.length === 0}
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
